/**
 * The calendar test's pending removals (DNKADM-0384 U3) — in memory, per staff id, for
 * the life of the app session.
 *
 * When a test appointment is created but the automatic delete fails, the server hands
 * back an explicit remove action and persists NOTHING (DNKAPI-0538 U3). The spec rules
 * that the removal "stays available until it is used": not hidden after a page change
 * within the console, and never cleaned up on a timer — the operator needs time to walk
 * over and look at a phone. So the panel parks the removal here rather than in its own
 * component state, which a route change or a save-remount would drop.
 *
 * In memory only, on purpose: the three fields are opaque provider routing keys (never
 * rendered, never a secret), and the console's storage posture is that nothing
 * app-state lives in web storage. A full browser reload therefore forgets a pending
 * removal — the coach can still delete the plainly-titled test appointment by hand.
 */
import type { StaffCalendarTestRemoval } from '@/api/staffCalendar';

const pending = new Map<string, StaffCalendarTestRemoval>();

/** The removal parked for this staff member, or `null`. */
export function getPendingCalendarTestRemoval(staffId: string): StaffCalendarTestRemoval | null {
  return pending.get(staffId) ?? null;
}

/** Park a removal for this staff member (a later test's removal replaces an earlier one). */
export function setPendingCalendarTestRemoval(
  staffId: string,
  removal: StaffCalendarTestRemoval,
): void {
  pending.set(staffId, removal);
}

/** Forget the parked removal once it has been used. */
export function clearPendingCalendarTestRemoval(staffId: string): void {
  pending.delete(staffId);
}

/** Test seam: forget every parked removal (per-test hygiene). */
export function __resetPendingCalendarTestRemovals(): void {
  pending.clear();
}
